import app from 'flarum/forum/app';
import safeFooterText from './util/safeFooterText';
import markAllAsRead from './util/markAllRead';

// Mithril global.

/**
 * Site-chrome features: announcement bar, custom footer, tag-driven colouring
 * (the ramon/colored replacement) and a user dark-mode toggle.
 */

function chromeDoc() {
  if (app.bespoke && app.bespoke.document) return app.bespoke.document;
  try {
    return app.forum.attribute('bespoke.published') || {};
  } catch (e) {
    return {};
  }
}
function editing() {
  return document.body.classList.contains('bespoke-editing');
}

// ---------- Announcement bar ----------
const AnnouncementBar = {
  view() {
    const a = chromeDoc().announce;
    if (!a || !a.enabled || !a.text) return null;
    if (!editing()) {
      try {
        if (localStorage.getItem('bespokeAnnounceHidden') === a.text) return null;
      } catch (e) {}
    }
    return m('.BespokeAnnounce', [m('span.BespokeAnnounce-text', a.link ? m('a', {
      href: a.link
    }, a.text) : a.text), m('button.BespokeAnnounce-close', {
      'aria-label': 'Dismiss',
      onclick: () => {
        try {
          localStorage.setItem('bespokeAnnounceHidden', a.text);
        } catch (e) {}
        m.redraw();
      }
    }, '×')]);
  }
};

// ---------- Footer ----------
// Most platforms use a Font Awesome brand icon; Packagist has none, so it uses
// the inline SVG marker 'packagist:svg' (rendered below). Its path is 12 KB, so
// it is a lazy chunk fetched only by a footer that links Packagist; the 1em box
// is drawn at once and the path fills in when it arrives.
let packagistPath = null;
let packagistLoading = false;
function packagistIconPath() {
  if (packagistPath === null && !packagistLoading) {
    packagistLoading = true;
    import(/* webpackChunkName: 'forum/packagistIcon', webpackMode: 'lazy-once' */ './packagistIcon').then(mod => {
      packagistPath = mod.PACKAGIST_ICON_PATH;
      m.redraw();
    }).catch(() => {
      packagistLoading = false;
    });
  }
  return packagistPath || undefined;
}
const SOCIAL = [['twitter', 'fab fa-x-twitter'], ['discord', 'fab fa-discord'], ['github', 'fab fa-github'], ['youtube', 'fab fa-youtube'], ['facebook', 'fab fa-facebook'], ['instagram', 'fab fa-instagram'], ['packagist', 'packagist:svg']];
const BespokeFooter = {
  view() {
    const f = chromeDoc().footer;
    if (!f || !f.enabled) return null;
    const links = (f.links || []).filter(l => l && l.label).map(l => m('a', {
      href: l.url || '#'
    }, l.label));
    // "Mark forums read" in the footer bar, as traditional forum software has
    // it: on every page, so a member never has to go back to the index for it.
    // An anchor, not a button, so it takes exactly the other links' styling,
    // the forum's own CSS for footer links included.
    if (app.session.user) {
      links.push(m('a.BespokeFooter-markRead', {
        href: '#',
        role: 'button',
        onclick: e => {
          e.preventDefault();
          markAllAsRead();
        }
      }, app.translator.trans('core.forum.index.mark_all_as_read_tooltip')));
    }
    const social = SOCIAL.filter(([k]) => f.social && f.social[k]);
    return m('.BespokeFooter', m('.BespokeFooter-inner.container', [links.length ? m('.BespokeFooter-links', links) : null, social.length ? m('.BespokeFooter-social', social.map(([k, icon]) => m('a', {
      href: f.social[k],
      'aria-label': k,
      target: '_blank',
      rel: 'noopener'
    }, icon === 'packagist:svg' ? m('svg', {
      viewBox: '0 0 24 24',
      width: '1em',
      height: '1em',
      fill: 'currentColor',
      style: 'vertical-align:-0.125em',
      'aria-hidden': 'true'
    }, m('path', {
      d: packagistIconPath()
    })) : m('i', {
      className: icon
    })))) : null,
    // A safe subset of HTML (line breaks, links, bold/italic, entities) —
    // never innerHTML. See util/safeFooterText.
    f.text ? m('.BespokeFooter-text', safeFooterText(String(f.text))) : null,
    /*
     * Flarum's own Custom Footer (Admin → Appearance), when it holds a
     * <div id="bespoke-footer-extra">, moves in here below the text. Core
     * writes that HTML into the page itself, outside the app and after it,
     * so a script in it (a Buy Me a Coffee button, which needs
     * document.write) runs as the page loads. Without this it would sit
     * below Bespoke's footer instead of inside it. The element is moved,
     * never copied, and Mithril leaves an empty vnode's DOM alone.
     */
    document.getElementById('bespoke-footer-extra') ? m('.BespokeFooter-extra', {
      oncreate: vnode => {
        const extra = document.getElementById('bespoke-footer-extra');
        if (extra) vnode.dom.appendChild(extra);
      }
    }) : null]));
  }
};

/** Mount the announcement bar (top) + footer (bottom) once. */
export function mountChrome() {
  if (!document.getElementById('bespoke-announce')) {
    const el = document.createElement('div');
    el.id = 'bespoke-announce';
    document.body.insertBefore(el, document.body.firstChild);
    m.mount(el, AnnouncementBar);
  }
  if (!document.getElementById('bespoke-footer')) {
    const el = document.createElement('div');
    el.id = 'bespoke-footer';
    document.body.appendChild(el);
    m.mount(el, BespokeFooter);
  }
}

// ---------- Tag colouring (ramon/colored) ----------
function currentTag() {
  try {
    // Canonical resolver from flarum/tags (handles slug → parent/child, lazy
    // load). Present whenever the Tags extension is enabled.
    if (typeof app.currentTag === 'function') {
      const t = app.currentTag();
      if (t) return t;
    }
    // Fallback: read the active tag slug straight off the search params.
    const params = app.search && app.search.state && app.search.state.params && app.search.state.params() || {};
    const slug = params.tags;
    if (slug) {
      const all = app.store.all('tags') || [];
      const t = all.find(x => x.slug && x.slug() === slug);
      if (t) return t;
    }
    // On a discussion page, use the discussion's primary tag.
    const disc = app.current && app.current.get && app.current.get('discussion') || null;
    if (disc && disc.tags) {
      const tags = disc.tags() || [];
      return tags.find(t => t && t.position && t.position() !== null) || tags[0] || null;
    }
  } catch (e) {}
  return null;
}

/** Readable ink (#fff / #111) for text laid over the given hex background. */
function readableInk(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#fff';
  const n = parseInt(m[1], 16);
  const ch = [n >> 16 & 255, n >> 8 & 255, n & 255].map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  const lum = 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  return lum > 0.45 ? '#111' : '#fff';
}
export function applyTagColor() {
  try {
    const el = document.documentElement;
    const r = el.style;
    const clear = () => {
      r.removeProperty('--bespoke-color-brand');
      r.removeProperty('--bespoke-tag-color');
      r.removeProperty('--bespoke-tag-text');
      el.removeAttribute('data-bespoke-tagactive');
    };
    const on = chromeDoc()?.tokens?.layout?.tagColors;
    if (!on || editing()) {
      clear();
      return;
    }
    const tag = currentTag();
    const color = tag && tag.color && tag.color();
    if (color) {
      // Drive the brand accent (buttons, links) AND a dedicated tag var that
      // the LESS uses to recolour the HERO — the ramon/colored behaviour,
      // theme-native. --bespoke-tag-text keeps the hero copy readable.
      r.setProperty('--bespoke-color-brand', color);
      r.setProperty('--bespoke-tag-color', color);
      r.setProperty('--bespoke-tag-text', readableInk(color));
      el.setAttribute('data-bespoke-tagactive', 'on');
    } else {
      clear();
    }
  } catch (e) {
    console.warn('[bespoke]', e);
  }
}

// ---------- User dark-mode scheme ----------
// The EFFECTIVE scheme right now: the data-theme Flarum core/Bespoke resolved
// (starts with "dark" → dark), else the OS preference. Used so the toggle/icon
// reflect what the visitor actually sees.
export function currentScheme() {
  const attr = document.documentElement.getAttribute('data-theme') || '';
  if (attr.indexOf('dark') === 0) return 'dark';
  if (attr.indexOf('light') === 0) return 'light';
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
export function toggleDarkScheme() {
  // Flip relative to what's actually showing.
  const next = currentScheme() === 'dark' ? 'light' : 'dark';
  // Drive Flarum CORE's color-scheme system rather than writing data-theme
  // ourselves. Core (and ernestdefoe/theme-toggle, which hooks setColorScheme)
  // is the single owner of data-theme and handles the no-flash pre-paint; a
  // second writer here is exactly what caused the load flash. savePreferences
  // persists the choice for logged-in members.
  try {
    const setScheme = app.setColorScheme;
    if (typeof setScheme === 'function') setScheme.call(app, next);else document.documentElement.setAttribute('data-theme', next); // very old core fallback
  } catch (e) {
    console.warn('[bespoke]', e);
  }
  try {
    app.session?.user?.savePreferences?.({
      colorScheme: next
    });
  } catch (e) {}
}flarum.reg.addChunkModule('ernestdefoe-bespoke:forum/packagistIcon', '377', 'ernestdefoe-bespoke', 'forum/packagistIcon');